import React from 'react';

import classNames from 'classnames';
import { faChevronRight, faFolderOpen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from 'components/ui/button';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { Textarea } from '@buildoutinc/blueprint-react/ui/Textarea';

const PROMPT_ID = 'ai-new-document-prompt';

export const EXAMPLE_PROMPTS = [
  'A broker opinion of value with an area overview',
  'An offering memorandum with demographics',
  'A one-page flyer',
];

interface Props {
  // A failure, kept out of the polite region so its role="alert" is announced once.
  alert?: React.ReactNode;
  disabled?: boolean;
  // Shown above the prompt: planning progress or the planner's reply.
  notice?: React.ReactNode;
  prompt: string;
  templateCount: number;
  onBrowseTemplates: () => void;
  onContinue: (prompt: string) => void;
  onPromptChange: (prompt: string) => void;
}

export function DescribeStep({
  alert,
  disabled = false,
  notice,
  prompt,
  templateCount,
  onBrowseTemplates,
  onContinue,
  onPromptChange,
}: Props) {
  const trimmedPrompt = prompt.trim();

  function browseTemplates() {
    if (!disabled) {
      onBrowseTemplates();
    }
  }

  function handleCardKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      browseTemplates();
    }
  }

  return (
    <>
      <Modal.Body className="d-flex flex-column gap-4">
        {alert}
        {/* Mounted while empty so screen readers announce what lands in it; hidden then so it takes no gap. */}
        <div aria-live="polite" className={classNames({ 'visually-hidden': !notice })}>
          {notice}
        </div>
        <Field>
          <Field.Label htmlFor={PROMPT_ID}>What do you want to build?</Field.Label>
          <Textarea
            autoFocus
            disabled={disabled}
            id={PROMPT_ID}
            placeholder="Tell us the document you want and what it should include…"
            rows={5}
            value={prompt}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onPromptChange(event.target.value)}
          />
          <div className="d-flex flex-wrap gap-2 mt-1">
            {EXAMPLE_PROMPTS.map(example => (
              <Button
                disabled={disabled}
                key={example}
                label={example}
                size="sm"
                variant="outline"
                onClick={() => onPromptChange(example)}
              />
            ))}
          </div>
        </Field>

        <div className="d-flex flex-column gap-2">
          <h3 className="form-label fs-body lh-base mb-0">Or start another way</h3>
          <Card
            aria-disabled={disabled}
            className={classNames({ clickable: !disabled, 'opacity-50': disabled })}
            role="button"
            tabIndex={disabled ? -1 : 0}
            onClick={browseTemplates}
            onKeyDown={handleCardKeyDown}
          >
            <Card.Body className="d-flex align-items-center gap-3">
              <FontAwesomeIcon className="text-accent" icon={faFolderOpen} />
              <div className="flex-grow-1">
                <div className="fw-semibold">Browse templates ({templateCount})</div>
                <div className="text-muted fs-small">Pick one of your saved, default, or company templates.</div>
              </div>
              <FontAwesomeIcon className="text-muted" icon={faChevronRight} />
            </Card.Body>
          </Card>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          className="me-auto"
          disabled={disabled}
          label="Create Manually"
          variant="secondary"
          onClick={browseTemplates}
        />
        <Button disabled={disabled || !trimmedPrompt} label="Continue" onClick={() => onContinue(trimmedPrompt)} />
      </Modal.Footer>
    </>
  );
}
